import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import TagManager from 'react-gtm-module';
import { Helmet } from 'react-helmet';
import { camelCase } from 'lodash';
import {
    CMSContent,
    IfNotMobile,
    StyleOverrides,
    scrollToHashElement,
    TeleScript,
    ErrorBoundary,
    getCMSObject,
    getInterpolatedPageData,
} from 'sg-ui-components';
import Footer from './Layouts/Footer';
import Header from './Layouts/Header';
import { mapStateToProps, mapDispatchToProps } from '../Store';
import IdleTimeout from './IdleTimeout';
import siteConfig from '../promotionConfig';

import LoggedInOnlyPages from '../Components/LoggedInOnlyPages';
import LoadingIndicator from './LoadingIndicator';

const Layout = ({ page, user, config, cmsSourceFirebase, actions, children, path, match, configReplacements }) => {
    const pageData = getInterpolatedPageData(config?.site?.routes, match, configReplacements);

    const errorBoundaryFallback = getCMSObject('data.messages.pageErrorMessages.jsonBlock')?.[`${camelCase(path) || 'home'}Fallback`];

    const layout = siteConfig.layout ?? {};

    //? Note: Frontend config will be requested and stored on first pageload / refresh
    useEffect(() => {
        async function configStateActions() {
            await actions.configActions.getEnvironmentConfig();
        }

        if (!config?.config?.firebase) {
            configStateActions();
            //? For debug: console.log('🟢 Initialization: Environment config set');
        }
    }, []);

    //? Note: Firebase CMS webContent and webMenu will be requested and stored on first pageload / refresh
    useEffect(() => {
        async function pageStateActions() {
            await actions.cmsSourceFirebaseActions.storeAllData({
                config: siteConfig?.testing?.firebase ?? config.config.firebase,
                docName: 'webConfig',
            });

            await actions.cmsSourceFirebaseActions.storeAllData({
                config: siteConfig?.testing?.firebase ?? config.config.firebase,
                docName: 'webContent',
            });

            await actions.cmsSourceFirebaseActions.storeAllData({
                config: siteConfig?.testing?.firebase ?? config.config.firebase,
                docName: 'webMenu',
            });

            await actions.cmsSourceFirebaseActions.setLoaded(true);

            const webConfigData = getCMSObject('data', 'webConfig') ?? {};
            const siteConfigWithOutFunction = JSON.parse(JSON.stringify(siteConfig));
            const siteConfigFromCMS = JSON.parse(webConfigData?.siteConfig ?? '{}') ?? {};
            const siteConfigData = { ...siteConfigWithOutFunction, ...siteConfigFromCMS };
            delete webConfigData.siteConfig;
            delete siteConfigData.testing;

            // Set config store
            await actions.configActions.setWebConfig(webConfigData);
            await actions.configActions.setSiteConfig(siteConfigData);
            await actions.configActions.setLoaded(true);

            scrollToHashElement();
        }

        if (config?.config?.firebase && !cmsSourceFirebase?.loaded) {
            pageStateActions();
            //? For debug: console.log('🟢 Initialization: CMS Data and Config Stores synced');
        }
    }, [config]);

    useEffect(() => {
        async function playerStateActions() {
            await actions.userActions.getPlayerData();
            scrollToHashElement();
        }

        playerStateActions();
        //? For debug: console.log('🟢 Initialization: Player validation completed');
    }, [user?.loggedIn]);

    // Get GTM ID based on the environment from the config api
    const gtmId = config?.config?.gtm_id;
    if (gtmId) {
        const tagManagerArgs = {
            gtmId,
        };
        TagManager.initialize(tagManagerArgs);
    }

    const isLoaded = config?.loaded && cmsSourceFirebase.loaded;

    //? Redirect players to login page if the page is logged in only page and the players are not logged in
    if (pageData?.loggedInOnly && !user.loggedIn) {
        return <LoggedInOnlyPages />;
    }

    if (!isLoaded) {
        return <LoadingIndicator priority='critical' />;
    }

    return (
        <>
            <Helmet defaultTitle='CT Lottery' titleTemplate='%s | CT Lottery'>
                <title>{pageData?.title}</title>
            </Helmet>
            <IfNotMobile>
                <CMSContent
                    cmsSourceFirebase={cmsSourceFirebase}
                    localStorageObject='webContent'
                    contentPath='data.webNotices'
                    className='fixed-top'
                    noticePlacement='web-notice-top-fixed'
                />
                <CMSContent
                    cmsSourceFirebase={cmsSourceFirebase}
                    localStorageObject='webContent'
                    contentPath='data.webNotices'
                    className=''
                    noticePlacement='web-notice-top'
                />
                <Header pageData={pageData} />
                <CMSContent
                    cmsSourceFirebase={cmsSourceFirebase}
                    localStorageObject='webContent'
                    contentPath='data.webNotices'
                    className=''
                    noticePlacement='web-notice-below-header'
                />
            </IfNotMobile>
            <ErrorBoundary fallback={<TeleScript line={errorBoundaryFallback} />} details={{ page, user, config }}>
                <div className={`content-root ${pageData?.className}`}>
                    <div className='page-content'>{children}</div>
                </div>
            </ErrorBoundary>
            <IfNotMobile>
                <CMSContent
                    cmsSourceFirebase={cmsSourceFirebase}
                    localStorageObject='webContent'
                    contentPath='data.webNotices'
                    className='fixed-bottom'
                    noticePlacement='web-notice-bottom-fixed'
                />
                <Footer secondaryMenu={layout?.mainMenu?.secondary_nav} copy={pageData.copy} actions={actions} user={user}></Footer>
            </IfNotMobile>

            <IdleTimeout />
            <StyleOverrides cmsSourceFirebase={cmsSourceFirebase} />
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(Layout);
